import type { Amount, Percent, Time } from '@df/common';
import { z } from 'zod';
import { amountSchema, percentSchema } from './units';

const keyFigureSchema = <TPeriodSchema extends z.ZodType, TQuantity extends z.ZodType>(
  periodSchema: TPeriodSchema,
  quantitySchema: TQuantity,
) =>
  z.object({
    first: quantitySchema,
    periods: z.map(
      periodSchema,
      z.object({
        accumulatedValue: quantitySchema,
        periodValue: quantitySchema,
      }),
    ),
  });
type KeyFigure = z.infer<
  ReturnType<
    typeof keyFigureSchema<
      z.ZodType<Time.YearMonth | Time.BookYear | Time.BookYear>,
      z.ZodType<Percent | Amount>
    >
  >
>;

const keyFiguresSchema = <TPeriodSchema extends z.ZodType>(periodSchema: TPeriodSchema) =>
  z.object({
    totalAssets: keyFigureSchema(periodSchema, amountSchema),
    grossProfit: keyFigureSchema(periodSchema, amountSchema),
    grossProfitMargin: keyFigureSchema(periodSchema, percentSchema),
    ebitda: keyFigureSchema(periodSchema, amountSchema),
    ebitdaMargin: keyFigureSchema(periodSchema, percentSchema),
    operatingProfit: keyFigureSchema(periodSchema, amountSchema),
    operatingProfitMargin: keyFigureSchema(periodSchema, percentSchema),
    workingCapital: keyFigureSchema(periodSchema, amountSchema),
    netCash: keyFigureSchema(periodSchema, amountSchema),
    unbookedResult: keyFigureSchema(periodSchema, amountSchema),
    equity: keyFigureSchema(periodSchema, amountSchema),
    solidityMargin: keyFigureSchema(periodSchema, percentSchema),
    ownerSalary: keyFigureSchema(periodSchema, amountSchema),
    revenue: keyFigureSchema(periodSchema, amountSchema),
    costOfLaborMargin: keyFigureSchema(periodSchema, percentSchema),
    quickRatioMargin: keyFigureSchema(periodSchema, percentSchema),
    inventoryTurnover: keyFigureSchema(periodSchema, percentSchema),

    costOfLabor: keyFigureSchema(periodSchema, amountSchema),
    financialNet: keyFigureSchema(periodSchema, amountSchema),
  });

export { keyFiguresSchema, type KeyFigure };
